import { useEffect, useState } from "react";

import { formatDateForInput } from "@/utils/dateUtils";
import { toast } from "sonner";

import DocumentUploadInput from "../../../components/shared/DocumentUploadInput";
import LazySearchSelect from "../../../components/shared/LazySearchSelect";
import WordCount from "../../../components/shared/WordCount.jsx";
import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Textarea } from "../../../components/ui/textarea";
import { useApi } from "../../../context/ApiContext";
import { useAuth } from "../../../context/AuthContext";
import { cn, getCharCount } from "../../../lib/utils.js";
import VendorDialog from "../../Vendors/component/VendorDialog";

const InvoiceDialog = ({
  isOpen,
  onOpenChange,
  onSuccess,
  selectedProject = "",
  selectedVendor = "",
  editingInvoice = null,
  vendors = [],
  initialAmount = ""
}) => {
  const api = useApi();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isVendorUser, setIsVendorUser] = useState(false);
  const [vendorUserDetail, setVendorUserDetail] = useState(null);

  const [selectedProjectDates, setSelectedProjectDates] = useState({
    startDate: "",
    endDate: ""
  });

  const [formData, setFormData] = useState({
    invoiceNumber: "",
    vendorId: selectedVendor || "",
    amount: initialAmount || "",
    dueDate: "",
    invoiceDate: new Date().toISOString().split("T")[0],
    status: "pending",
    notes: "",
    projectId: selectedProject || "",
    attachmentUrls: []
  });

  const [isVendorDialogOpen, setIsVendorDialogOpen] = useState(false);

  const isNotesOverLimit = getCharCount(formData.notes) > 500;

  // Check if current user email matches a vendor email
  useEffect(() => {
    const checkVendorStatus = async () => {
      if (user && user.email) {
        try {
          const res = await api.get(`/vendors?page=0&size=1000`);
          const vendorList = res.data?.content || res.data || [];
          const matched = vendorList.find((v) => v.email && v.email.toLowerCase() === user.email.toLowerCase());
          if (matched) {
            setIsVendorUser(true);
            setVendorUserDetail(matched);
            if (!editingInvoice) {
              setFormData((prev) => ({ ...prev, vendorId: matched.id }));
            }
          }
        } catch (err) {
          console.error("Error fetching vendors for vendor check", err);
        }
      }
    };
    if (isOpen) {
      checkVendorStatus();
    }
  }, [user, api, isOpen, editingInvoice]);

  useEffect(() => {
    if (editingInvoice) {
      setFormData({
        invoiceNumber: editingInvoice.invoiceNumber || "",
        vendorId: editingInvoice.vendorId || "",
        amount: editingInvoice.amount?.toString() || "",
        dueDate: editingInvoice.dueDate ? new Date(editingInvoice.dueDate).toISOString().split("T")[0] : "",
        invoiceDate: editingInvoice.invoiceDate
          ? new Date(editingInvoice.invoiceDate).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
        status: editingInvoice.status || "pending",
        notes: editingInvoice.notes || "",
        projectId: editingInvoice.project?.id || editingInvoice.projectId || selectedProject || "",
        attachmentUrls: editingInvoice.attachmentUrls || []
      });
    } else {
      setFormData({
        invoiceNumber: "",
        vendorId: selectedVendor || (vendorUserDetail ? vendorUserDetail.id : ""),
        amount: initialAmount || "",
        dueDate: "",
        invoiceDate: new Date().toISOString().split("T")[0],
        status: "pending",
        notes: "",
        projectId: selectedProject || "",
        attachmentUrls: []
      });
    }
  }, [editingInvoice, selectedProject, selectedVendor, isOpen, initialAmount, vendorUserDetail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isNotesOverLimit) {
      toast.error("Notes exceed the 500-character limit.");
      return;
    }
    if (!formData.projectId) {
      toast.error("Please select a project");
      return;
    }
    if (!formData.vendorId) {
      toast.error("Please select a vendor");
      return;
    }
    setLoading(true);
    try {
      const amount = parseFloat(formData.amount);
      let response;
      if (editingInvoice) {
        response = await api.put(`/invoices/${editingInvoice.id}`, {
          ...formData,
          amount: amount,
          taxAmount: editingInvoice.taxAmount || 0,
          totalAmount: amount + (editingInvoice.taxAmount || 0)
        });
        toast.success("Invoice updated successfully!");
      } else {
        response = await api.post(`/invoices`, {
          ...formData,
          amount: amount,
          taxAmount: 0,
          totalAmount: amount
        });
        toast.success("Invoice created successfully!");
      }

      if (onSuccess) onSuccess(response.data);
      onOpenChange(false);
    } catch (error) {
      toast.error(error.message || "Error saving invoice");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0 overflow-hidden flex flex-col">
        <div className="p-6 pb-0">
          <DialogHeader>
            <DialogTitle>{editingInvoice ? "Edit Invoice" : "Create New Invoice"}</DialogTitle>
            <DialogDescription>{editingInvoice ? "Update invoice information" : "Add a new vendor invoice"}</DialogDescription>
          </DialogHeader>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>
                  Associated Project <span className="text-red-500">*</span>
                </Label>
                <LazySearchSelect
                  value={formData.projectId || "all"}
                  onChange={(val, project) => {
                    setFormData({
                      ...formData,
                      projectId: val === "all" ? "" : val
                    });
                    if (project) {
                      setSelectedProjectDates({
                        startDate: project.startDate || "",
                        endDate: project.endDate || ""
                      });
                    }
                  }}
                  placeholder="Select Project"
                  apiUrl="/projects"
                  showAllOption={true}
                  allOptionLabel="No Project"
                  popoverWidth="w-[var(--radix-popover-trigger-width)] md:w-[400px]"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label htmlFor="vendorId">
                    Vendor <span className="text-red-500">*</span>
                  </Label>
                  {!editingInvoice && !isVendorUser && (
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-xs text-primary"
                      onClick={() => setIsVendorDialogOpen(true)}
                    >
                      + Add New
                    </Button>
                  )}
                </div>
                <LazySearchSelect
                  value={formData.vendorId}
                  onChange={(val) => setFormData({ ...formData, vendorId: val })}
                  placeholder="Select Vendor"
                  searchPlaceholder="Search vendors..."
                  apiUrl="/vendors"
                  labelKey="vendorName"
                  disabled={isVendorUser || (!!selectedVendor && !editingInvoice)}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="invoiceNumber">Invoice Number</Label>
                <Input
                  id="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
                  placeholder="Enter invoice number"
                />
              </div>
              <div>
                <Label htmlFor="amount">
                  Amount <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="invoiceDate">Invoice Date</Label>
                <Input
                  type="date"
                  id="invoiceDate"
                  value={formData.invoiceDate}
                  max={formatDateForInput(selectedProjectDates.endDate)}
                  onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="dueDate">
                  Due Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="date"
                  id="dueDate"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  required
                />
              </div>
            </div>
            {editingInvoice && (
              <div>
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(val) => setFormData({ ...formData, status: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="denied">Denied</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Internal notes or additional details"
                className={cn(isNotesOverLimit && "border-red-500")}
                rows={3}
              />
              <WordCount text={formData.notes} />
            </div>

            <DocumentUploadInput
              id="invoice-attachment-upload"
              value={formData.attachmentUrls || []}
              onChange={(val) => setFormData({ ...formData, attachmentUrls: val })}
              label="Attachments (Optional)"
              multiple={true}
              helperText="Upload files or images related to this invoice"
            />
          </div>
          <div className="p-6 pt-2 border-t bg-white dark:bg-yale-blue-900 sticky bottom-0 z-10">
            <Button
              type="submit"
              variant="primary"
              className="w-full py-6 rounded-lg shadow-lg"
              disabled={loading || isNotesOverLimit}
            >
              {loading ? "Saving..." : editingInvoice ? "Update Invoice" : "Create Invoice"}
            </Button>
          </div>
        </form>
      </DialogContent>

      <VendorDialog
        isOpen={isVendorDialogOpen}
        onOpenChange={setIsVendorDialogOpen}
        onSuccess={(newVendor) => {
          setFormData((prev) => ({ ...prev, vendorId: newVendor.id }));
        }}
      />
    </Dialog>
  );
};

export default InvoiceDialog;
